import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';
import Layout from '@/components/Layout';
import Generate from '@/pages/Generate';
import MyVideos from '@/pages/MyVideos';
import Credits from '@/pages/Credits';
import Account from '@/pages/Account';
import AccountHistory from '@/pages/AccountHistory';
import AdminConfig from '@/pages/AdminConfig';
import ClaimGift from '@/pages/ClaimGift';
import PrivacyPolicy from '@/pages/PrivacyPolicy';
import TermsOfService from '@/pages/TermsOfService';
import RefundPolicy from '@/pages/RefundPolicy';
import AuthCallback from '@/pages/AuthCallback';
import MaintenancePage from '@/pages/MaintenancePage';
import Tutorials from '@/pages/Tutorials';
import GeoGate from '@/components/GeoGate';
import { AuthProvider } from '@/context/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function GoogleAnalytics() {
  useEffect(() => {
    axios.get(`${API}/analytics/ga-id`).then(res => {
      const gaId = res.data?.ga_measurement_id;
      if (!gaId) return;
      // Inject gtag script
      const script1 = document.createElement('script');
      script1.async = true;
      script1.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
      document.head.appendChild(script1);

      const script2 = document.createElement('script');
      script2.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${gaId}');`;
      document.head.appendChild(script2);
    }).catch(() => {});
  }, []);
  return null;
}

/**
 * Site-wide maintenance gate. Polls `/api/maintenance` and, when enabled,
 * shows MaintenancePage for everything EXCEPT `/admin/config` (admin tools
 * must stay reachable so the admin can flip the switch back).
 */
function MaintenanceGate({ children }) {
  const location = useLocation();
  const [state, setState] = useState({ checked: false, enabled: false, message: '' });

  useEffect(() => {
    let cancelled = false;
    const fetchState = () => {
      axios.get(`${API}/maintenance`)
        .then(res => {
          if (cancelled) return;
          setState({
            checked: true,
            enabled: !!res.data?.enabled,
            message: res.data?.message || '',
          });
        })
        .catch(() => {
          // Fail open — if we can't reach the API, don't trap the user on a fake
          // maintenance page (the real API errors will surface organically).
          if (!cancelled) setState({ checked: true, enabled: false, message: '' });
        });
    };
    fetchState();
    const interval = setInterval(fetchState, 15000); // re-check every 15s
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  // Admin route always passes through — no flicker, no probe required.
  if (location.pathname.startsWith('/admin')) {
    return children;
  }

  // While the first probe is in-flight, render the app optimistically. The
  // maintenance check only takes ~100ms so users won't notice; if it comes back
  // enabled, the page swaps to MaintenancePage on the next tick.
  if (state.checked && state.enabled) {
    return <MaintenancePage message={state.message} />;
  }
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <GoogleAnalytics />
        <GeoGate>
          <div className="App">
            <MaintenanceGate>
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<Generate />} />
                  <Route path="generate" element={<Navigate to="/" />} />
                  <Route path="loop" element={<Generate />} />
                  <Route path="stabilize" element={<Generate />} />
                  <Route path="image" element={<Generate />} />
                  <Route path="sound" element={<Generate />} />
                  <Route path="atmosphere" element={<Generate />} />
                  <Route path="overlays" element={<Generate />} />
                  <Route path="upscale" element={<Generate />} />
                  <Route path="transform" element={<Generate />} />
                  <Route path="rain" element={<Generate />} />
                  <Route path="my-gallery" element={<MyVideos />} />
                  <Route path="my-videos" element={<Navigate to="/my-gallery" />} />
                  <Route path="credits" element={<Credits />} />
                  <Route path="account" element={<Account />} />
                  <Route path="account/history" element={<AccountHistory />} />
                  <Route path="verify-email" element={<Account />} />
                  <Route path="claim-gift" element={<ClaimGift />} />
                  <Route path="reset-password" element={<Account />} />
                  <Route path="privacy" element={<PrivacyPolicy />} />
                  <Route path="terms" element={<TermsOfService />} />
                  <Route path="refund-policy" element={<RefundPolicy />} />
                  <Route path="tutorials" element={<Tutorials />} />
                </Route>
                <Route path="/admin/config" element={<AdminConfig />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
              </Routes>
            </MaintenanceGate>
            <Toaster position="bottom-center" richColors />
          </div>
        </GeoGate>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
